import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { saveAs } from "file-saver";
import moment from "moment";
import unsplash from "../api/unsplash";
import { consulenzaAuth } from "./useConsulenzaData";

const aree = {
  sicurezza: "Sicurezza",
  sistemi_gestione: "Sistemi di gestione",
  igiene_alimentare: "Igiene alimentare",
  coordinamento_sanitario: "Coordinamento sanitario",
};
export default function ImportContrattiConsulenza({
  medicina = false,
  onImportato,
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [righe, setRighe] = useState(null);
  const [preview, setPreview] = useState(null);
  const [confermaAvvisi, setConfermaAvvisi] = useState(false);
  const version = useRef(0);
  useEffect(
    () => () => {
      version.current += 1;
    },
    [],
  );
  const path = (action) =>
    `contrattiConsulenza/import/${action}${medicina ? "?settore=medicina" : ""}`;
  const download = async () => {
    setBusy(true);
    setError("");
    try {
      const catalogo = await unsplash.get(path("catalogo"), consulenzaAuth());
      const { creaWorkbookContratti } = await import("./contrattiWorkbook");
      const wb = creaWorkbookContratti(catalogo.data.data, medicina);
      const buffer = await wb.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        medicina
          ? "modello-coordinamento-sanitario.xlsx"
          : "modello-contratti-consulenza.xlsx",
      );
    } catch (e) {
      setError(
        e.response?.data?.message || "Impossibile scaricare il modello.",
      );
    } finally {
      setBusy(false);
    }
  };
  const anteprima = async (rows) => {
    const response = await unsplash.post(
      path("anteprima"),
      { righe: rows },
      consulenzaAuth(),
    );
    return response.data.data;
  };
  const carica = async (file) => {
    if (!file) return;
    const current = ++version.current;
    setBusy(true);
    setError("");
    setRighe(null);
    setPreview(null);
    setConfermaAvvisi(false);
    setSuccess("");
    try {
      if (!/\.xlsx$/i.test(file.name) || file.size > 10 * 1024 * 1024)
        throw new Error("Scegli un file .xlsx fino a 10 MB.");
      const { leggiWorkbookContratti } = await import("./contrattiWorkbook");
      const rows = await leggiWorkbookContratti(await file.arrayBuffer());
      const checked = await anteprima(rows);
      if (current === version.current) {
        setRighe(rows);
        setPreview(checked);
      }
    } catch (e) {
      if (current === version.current)
        setError(
          e.response?.data?.message || e.message || "File non leggibile.",
        );
    } finally {
      if (current === version.current) setBusy(false);
    }
  };
  const ricontrolla = async () => {
    setBusy(true);
    setError("");
    setConfermaAvvisi(false);
    try {
      setPreview(await anteprima(righe));
    } catch (e) {
      setError(e.response?.data?.message || "Impossibile verificare le righe.");
    } finally {
      setBusy(false);
    }
  };
  const conferma = async () => {
    setBusy(true);
    setError("");
    try {
      const response = await unsplash.post(
        path("conferma"),
        { righe, conferma: true, confermaAvvisi },
        consulenzaAuth(),
      );
      setSuccess(
        `Importati ${response.data.data.importati} contratti. Nessun contratto esistente è stato modificato.`,
      );
      setRighe(null);
      setPreview(null);
      setOpen(false);
      await onImportato?.();
    } catch (e) {
      setError(
        e.response?.data?.message ||
          "Importazione non riuscita. Nessun contratto è stato salvato.",
      );
      setConfermaAvvisi(false);
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Stack spacing={1}>
      <Button
        variant="outlined"
        onClick={() => {
          setOpen(true);
          setError("");
        }}
      >
        Importa contratti da Excel
      </Button>
      {success && <Alert severity="success">{success}</Alert>}
      <Dialog
        open={open}
        onClose={() => !busy && setOpen(false)}
        fullWidth
        maxWidth="xl"
      >
        <DialogTitle>
          Importazione contratti{" "}
          {medicina ? "di coordinamento sanitario" : "di consulenza"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography>
              Scarica il modello con i clienti accessibili al tuo utente e le
              tendine. Compila le righe e carica il file per controllare
              l’anteprima prima di confermare.
            </Typography>
            <Alert severity="info">
              Per i contratti in mantenimento conserva firma e decorrenza
              originarie: dal secondo anno si applica il canone rinnovi. Non
              usare la data di caricamento. La fine effettiva serve solo per
              interrompere le quote. Il file crea nuovi contratti; le modifiche
              si fanno dalla scheda cliente.
            </Alert>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <Button disabled={busy} onClick={download}>
                Scarica modello
              </Button>
              <Button component="label" variant="contained" disabled={busy}>
                Carica file per anteprima
                <input
                  hidden
                  type="file"
                  accept=".xlsx"
                  aria-label="File contratti Excel"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = "";
                    carica(file);
                  }}
                />
              </Button>
              {righe && (
                <Button disabled={busy} onClick={ricontrolla}>
                  Ricalcola anteprima
                </Button>
              )}
            </Box>
            {busy && (
              <CircularProgress
                size={24}
                aria-label="Elaborazione importazione"
              />
            )}
            {error && <Alert severity="error">{error}</Alert>}
            {preview && (
              <>
                <Typography>
                  {preview.righe.length} righe compilate · {preview.errori} con
                  errori · {preview.avvisi} con segnalazioni
                </Typography>
                {preview.errori > 0 && (
                  <Alert severity="error">
                    Correggi il file e ricaricalo: con errori non viene
                    importata alcuna riga.
                  </Alert>
                )}
                <TableContainer sx={{ maxHeight: 450 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {[
                          "Riga",
                          "Cliente",
                          "Area / norme",
                          "Firma / decorrenza / fine",
                          "Canoni annui primo anno / rinnovi",
                          "Periodicità / emissione",
                          "Consulente ID",
                          "Adeguamento ISTAT contratto",
                          "Note interne / fatturazione",
                          "Controlli",
                        ].map((h) => (
                          <TableCell key={h}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {preview.righe.map((r, i) => {
                        const c = r.contratto;
                        const date = (v) =>
                          v ? moment(v).format("DD/MM/YYYY") : "—";
                        return (
                          <TableRow key={i}>
                            <TableCell>{r.riga}</TableCell>
                            <TableCell>
                              {r.ragioneSociale} (ID {r.cliente})
                            </TableCell>
                            <TableCell>
                              {aree[c?.Area]}
                              {c?.Norme?.length > 0 && (
                                <Typography variant="caption" display="block">
                                  {c.Norme.join(", ")}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {c &&
                                `${date(c.DataFirma)} / ${date(c.DataInizio)} / ${date(c.DataFine)}`}
                            </TableCell>
                            <TableCell>
                              {c &&
                                `${c.CanonePrimoAnno} € / ${c.CanoneRinnovi} €`}
                            </TableCell>
                            <TableCell>
                              {c &&
                                `Ogni ${c.CadenzaMesi} mesi / ${c.ModalitaEmissione}`}
                            </TableCell>
                            <TableCell>{c?.Responsabile}</TableCell>
                            <TableCell>
                              {Number(c?.AdeguamentoIstat) === 1 ? "Sì" : "No"}
                            </TableCell>
                            <TableCell>
                              {c?.Note || "—"} / {c?.NoteFatturazione || "—"}
                            </TableCell>
                            <TableCell>
                              {!r.errori.length && !r.avvisi.length ? (
                                "Da importare"
                              ) : (
                                <>
                                  {r.errori.map((e, n) => (
                                    <Typography
                                      key={`e${n}`}
                                      color="error"
                                      variant="body2"
                                    >
                                      {e}
                                    </Typography>
                                  ))}
                                  {r.avvisi.map((a, n) => (
                                    <Typography
                                      key={`a${n}`}
                                      color="warning.main"
                                      variant="body2"
                                    >
                                      {a}
                                    </Typography>
                                  ))}
                                </>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                {preview.avvisi > 0 && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={confermaAvvisi}
                        disabled={busy || preview.errori > 0}
                        onChange={(e) => setConfermaAvvisi(e.target.checked)}
                      />
                    }
                    label="Ho verificato le segnalazioni e confermo le righe da importare"
                  />
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setOpen(false)}>
            Chiudi
          </Button>
          <Button
            variant="contained"
            disabled={
              busy ||
              !preview ||
              preview.errori > 0 ||
              (preview.avvisi > 0 && !confermaAvvisi)
            }
            onClick={conferma}
          >
            Conferma importazione{preview ? ` (${preview.righe.length})` : ""}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
